/***
 *
 *   CATEGORY MULTI-SELECT
 *   Toolbar category filter. Ported from
 *   ui-reference/src/components/tickets/category-multi-select.tsx.
 *
 *   This app stores category selection in the `c` search param (single id or
 *   "all"). Multi-select writes comma-separated ids; an empty selection means
 *   all.
 *
 *   PROPS
 *   categories: ticket_categories live-query result (or { data })
 *   tickets: all tickets for counts (optional)
 *   selected: string[] of category ids (empty = all)
 *   onChange: (ids: string[] | null) => void  — null/[] means all
 *   projectId: for manage-categories link
 *
 *   The edit / manage-categories affordances are gated on the SAME permission
 *   the routes behind them require (`/app/settings/projects/:id` is `admin`), so
 *   the menu never offers a link that redirects straight back to /signin. Read
 *   it off AuthContext's resolved permission map rather than string-matching
 *   `user.permission` — that missed `master`, which the map grants admin to.
 **********/

import { Badge, Menu, Icon, cn } from 'components/lib';
import { useTranslate } from 'app/translations';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from 'app/auth';
import { useContext } from 'react';
import { findActiveTickets } from 'utilities/tickets';

export function CategoryMultiSelect(props) {
    const { _t } = useTranslate();
    const navigate = useNavigate();
    const { permission } = useContext(AuthContext);

    // Live-query objects are truthy before `data` arrives — only treat a real
    // array as the category list, otherwise `.map` throws on the query shape.
    const categories = Array.isArray(props.categories?.data)
        ? props.categories.data
        : Array.isArray(props.categories)
          ? props.categories
          : [];
    const selected = props.selected || [];
    const selectedSet = selected.length > 0 ? new Set(selected) : null;
    const tickets = findActiveTickets(Array.isArray(props.tickets) ? props.tickets : []) || [];

    function countForCategory(categoryId) {
        return tickets.filter((t) => t.ticket_category_id === categoryId).length;
    }

    function toggle(id, checked) {
        if (checked) {
            const next = selected.length ? [...selected, id] : [id];
            props.onChange?.(next);
            return;
        }
        const next = selected.filter((x) => x !== id);
        props.onChange?.(next.length === 0 ? null : next);
    }

    const canManage = !!permission?.admin;

    const list = [
        {
            label: _t('ticket.filters.categoriesAll'),
            action: () => props.onChange?.(null),
            className: selected.length === 0 ? 'font-medium text-primary-600' : undefined,
        },
        { separator: true },
        ...categories.map((category) => ({
            label: (
                <span className="flex w-full min-w-0 items-center justify-between gap-2">
                    <span className="flex min-w-0 items-center gap-2">
                        <span
                            className={cn(
                                'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border',
                                selectedSet?.has(category.id)
                                    ? 'border-primary-600 bg-primary-600 text-white'
                                    : 'border-border',
                            )}
                            aria-hidden>
                            {selectedSet?.has(category.id) && (
                                <Icon image="check" size={10} color="currentColor" />
                            )}
                        </span>
                        <span className="min-w-0 truncate">{category.name}</span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                        <span className="text-xs text-text-muted">{countForCategory(category.id)}</span>
                        {canManage && (
                            <button
                                type="button"
                                className="rounded p-0.5 text-text-muted hover:bg-muted hover:text-text-secondary"
                                title={_t('ticket.filters.editCategory')}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    navigate(`/app/tickets/category/${category.id}`);
                                }}>
                                <Icon image="edit-2" size={12} color="currentColor" />
                            </button>
                        )}
                    </span>
                </span>
            ),
            action: () => toggle(category.id, !selectedSet?.has(category.id)),
        })),
        ...(selected.length > 0
            ? [
                  { separator: true },
                  {
                      label: _t('ticket.filters.categoriesClear'),
                      action: () => props.onChange?.(null),
                  },
              ]
            : []),
        ...(canManage && props.projectId
            ? [
                  { separator: true },
                  {
                      label: (
                          <span className="flex items-center gap-2 font-medium text-primary-600 dark:text-primary-400">
                              <Icon image="settings" size={14} color="currentColor" />
                              {_t('ticket.filters.manageCategories')}
                          </span>
                      ),
                      action: () =>
                          navigate(`/app/settings/projects/${props.projectId}#ticket-categories`),
                  },
              ]
            : []),
    ];

    return (
        <Menu
            align="start"
            width="w-72"
            label={_t('ticket.filters.categories')}
            list={list}
            triggerClassName={cn(
                'h-9 gap-2 rounded-md border border-input bg-surface px-3 text-xs font-medium',
                'text-text-primary shadow-sm hover:bg-primary-50',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                'dark:hover:bg-primary-900/40',
            )}
            trigger={
                <>
                    <Icon image="tag" size={14} color="currentColor" />
                    {_t('ticket.filters.categories')}
                    {selected.length > 0 && (
                        <Badge
                            text={String(selected.length)}
                            variant="count"
                            /* Same circular pip Button renders for `count` — the
                               two sit side by side in this toolbar. */
                            className="h-4 min-w-[1rem] justify-center px-1 py-0 text-[10px]"
                        />
                    )}
                    <Icon image="chevron-down" size={14} color="currentColor" className="opacity-70" />
                </>
            }
        />
    );
}
